import type { BridgeRequest, BridgeResponse, DocumentState, FairlightBridge } from './types';

export class PyodideBridge implements FairlightBridge {
  private worker = new Worker(new URL('./fairlight.worker.ts', import.meta.url), { type: 'module', name: 'fairlight-python' });
  private nextId = 1;
  private pending = new Map<number, { resolve(value: any): void; reject(error: Error): void }>();
  private readyPromise: Promise<void>;
  private readyResolve!: () => void;
  private readyReject!: (error: Error) => void;

  constructor() {
    this.readyPromise = new Promise((resolve, reject) => { this.readyResolve = resolve; this.readyReject = reject; });
    this.worker.onmessage = (event: MessageEvent<any>) => {
      if (event.data.id === 0) {
        event.data.ready ? this.readyResolve() : this.readyReject(new Error(event.data.error));
        return;
      }
      const callback = this.pending.get(event.data.id); if (!callback) return;
      this.pending.delete(event.data.id);
      const response = event.data.response as BridgeResponse<unknown>;
      if (response.ok && (event.data.pcmBuffer || event.data.laneBuffers)) {
        if (event.data.pcmBuffer) (response.result as Record<string, unknown>).pcmBuffer = event.data.pcmBuffer;
        if (event.data.laneBuffers) (response.result as Record<string, unknown>).laneBuffers = event.data.laneBuffers;
      }
      response.ok ? callback.resolve(response.result) : callback.reject(bridgeError(response.error));
    };
    this.worker.onerror = (event) => this.readyReject(new Error(event.message));
  }

  ready() { return this.readyPromise; }

  async request<T>(request: BridgeRequest, transfer: Transferable[] = []): Promise<T> {
    await this.ready(); const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage({ id, request, buffer: transfer[0] }, transfer);
    });
  }

  async openMedia(file: File) {
    const buffer = await file.arrayBuffer();
    return this.request<DocumentState>({ action: 'open_media', filename: file.name }, [buffer]);
  }

  dispose() { this.worker.terminate(); }
}

// Keep legacy errors intact; known Instrument refusals get operator-facing copy.
export function bridgeError(error: { type: string; message: string; code?: string; details?: Record<string, unknown> }) {
  let message = error.message;
  if (error.type === 'InstrumentMutationError' && error.code === 'UNGENERATED_PITCH_INDEX_109') {
    message = 'This tuning cannot be written safely because one or more keys reach an undefined original Fairlight pitch-table boundary. No changes were made.';
    message += ` Technical detail: ${error.message}.`;
  } else if (error.type === 'InstrumentMutationError' && error.code === 'UNSUPPORTED_WRAPPED_ROOT_DOMAIN') {
    message = 'Scale authoring supports Root 1–128. The stored historical value is preserved unchanged.';
  }
  return Object.assign(new Error(message), { code: error.code, details: error.details, type: error.type });
}
